import React, {
  useContext, useEffect, useMemo, useState,
} from 'react';
import { useLocation } from 'react-router';
import PropTypes from 'prop-types';
import {
  Popover, Switch, Typography, IconButton,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { Box } from '@mui/system';
import { ActivityType } from '../../propTypes';
import { sendRequest } from '../../api/shootAPI';
import API from '../../api/endpoints';
import { getActivityColor } from '../../utils/activity';
import ScreenshotViewer from './ScreenshotViewer';
import { UserContext } from '../../contexts/User';

function Snapshot({
  snapshot, blurScreenshots, isAdmin, onDeleteActivity, deleting,
}) {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const memberId = queryParams.get('member');
  const { member } = useContext(UserContext);
  const {
    screenshots, activityPercentage, secondsElapsed, mousePercentage, keyboardPercentage,
    meetingPercentage,
  } = snapshot.data;

  const [isBlur, setIsBlur] = useState(blurScreenshots);
  const [screensAreOpen, setScreensAreOpen] = useState(false);
  const [coverImageUrl, setCoverImageUrl] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [isHovered, setIsHovered] = useState(false);
  const open = Boolean(anchorEl);

  const handlePopoverOpen = (event) => setAnchorEl(event.currentTarget);
  const handlePopoverClose = () => setAnchorEl(null);

  useEffect(() => {
    if (screenshots && screenshots.length) {
      const fetchCoverImage = async () => {
        const { success, data } = await sendRequest({
          method: 'GET',
          url: API.MEMBER.screenshot(memberId),
          params: { baseUrl: screenshots[0].url },
        });

        if (success) setCoverImageUrl(data.url);
      };
      fetchCoverImage();
    }
  }, [screenshots, memberId]);

  const formattedTime = useMemo(() => {
    const duration = secondsElapsed < 60
      ? `${secondsElapsed} seconds`
      : `${Math.round(secondsElapsed / 60)} minutes`;

    return `${activityPercentage}% of ${duration}`;
  }, [activityPercentage, secondsElapsed]);

  return (
    <>
      <article
        className="snapshot flex-col"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events */}
        <div
          style={{
            position: 'relative', height: '100%', width: '100%', background: 'var(--dark-5)',
          }}
          className="snapshot-img-view flex-row align-c jstfy-c"
          onClick={() => {
            if (!isBlur) {
              setScreensAreOpen(true);
            }
          }}
          role="button"
          tabIndex={0}
        >
          <div className="snapshot-img-cover" />
          {coverImageUrl ? (
            <div style={{ position: 'relative', width: '100%' }}>
              <img
                src={coverImageUrl}
                width="100%"
                alt="Activity screenshot"
                className={isBlur ? 'snapshot-img-blur' : ''}
                style={{ borderRadius: '4px 4px 0 0', minHeight: 68 }}
              />
              <div style={{
                position: 'absolute',
                bottom: 0,
                left: '50%',
                transform: 'translateX(-50%)',
                backgroundColor: 'rgba(0, 0, 0, 0.6)',
                color: 'white',
                padding: '2px 8px',
                fontSize: 12,
                borderRadius: '4px',
              }}
              >
                {screenshots.length && screenshots.length > 1 ? screenshots.length : ''}
                {' '}
                Screens
              </div>
            </div>
          ) : (
            <p style={{
              minHeight: 68,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: 0,
            }}
            >
              No Screenshots
            </p>
          )}
          {/* Delete button on hover - only for admins, always visible on card hover */}
          {isAdmin && isHovered && (
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                onDeleteActivity(snapshot.data.id);
              }}
              sx={{
                position: 'absolute',
                top: 4,
                right: 4,
                backgroundColor: 'rgba(244, 67, 54, 0.9)',
                color: 'white',
                padding: '4px',
                zIndex: 2,
                '&:hover': {
                  backgroundColor: 'rgba(211, 47, 47, 1)',
                },
              }}
              size="small"
              disabled={!!deleting}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          )}
        </div>

        <div className="snapshot-details" style={{ marginTop: 'auto' }}>
          <div className="snap-text">
            {snapshot.roundedStartTime}
            {' '}
            -
            {' '}
            {snapshot.roundedEndTime}
          </div>
          <div
            className="activity-bar-container"
            aria-owns={!snapshot.isManual && open ? 'mouse-over-popover' : undefined}
            aria-haspopup={!snapshot.isManual ? 'true' : undefined}
            onMouseEnter={!snapshot.isManual ? handlePopoverOpen : undefined}
            onMouseLeave={!snapshot.isManual ? handlePopoverClose : undefined}
          >
            <div
              className="activity-bar-parent"
            >
              <div
                className="activity-bar"
                style={{
                  width: `${activityPercentage}%`,
                  // eslint-disable-next-line no-nested-ternary,max-len
                  background: activityPercentage > 50 ? '#64BA62' : activityPercentage > 20 ? '#F6C643' : '#EE7C5C',
                }}
              />
            </div>
          </div>
          {snapshot.isManual === false && (
            <>
              <div className="snap-text">
                {formattedTime}
              </div>
              {(member && ((member.id === memberId) || (member.accountType === 'Primary owner'))) && (
                <div className="d-flex justify-content-center align-items-center">
                  <Switch size="small" checked={isBlur} onChange={() => setIsBlur(!isBlur)} color="secondary" />
                  <span className="snap-text">Blur Screenshot</span>
                </div>
              )}
            </>
          )}

          {!!snapshot.isManual && (
            <div
              className="snap-text"
              style={{
                position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              }}
            >
              <span>Manual Activity</span>
              {/* Delete button for manual activities - only for admins */}
              {isAdmin && isHovered && (
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    onDeleteActivity(snapshot.data.id);
                  }}
                  sx={{
                    backgroundColor: 'rgba(244, 67, 54, 0.9)',
                    color: 'white',
                    padding: '4px',
                    marginLeft: '4px',
                    '&:hover': {
                      backgroundColor: 'rgba(211, 47, 47, 1)',
                    },
                  }}
                  size="small"
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
            </div>
          )}
        </div>

        <Popover
          id={`activity-popover-${snapshot.data.id}`}
          sx={{
            pointerEvents: 'none',
            '& .MuiPaper-root': {
              backgroundColor: '#1E1E1E',
              color: 'white',
              borderRadius: '6px',
              minWidth: '120px',
              textAlign: 'center',
              p: 1,
              boxShadow: '0 2px 6px rgba(255,255,255,0.15)',
            },
          }}
          open={open}
          anchorEl={anchorEl}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          transformOrigin={{ vertical: 'top', horizontal: 'center' }}
          onClose={handlePopoverClose}
          disablePortal
          disableEnforceFocus
          disableRestoreFocus
        >
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.3, fontSize: '10px' }}>
              Breakdown
            </Typography>
            <Typography
              variant="body1"
              sx={{ fontWeight: 'bold', color: getActivityColor(activityPercentage), fontSize: '12px' }}
            >
              {activityPercentage}
              %
            </Typography>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                gap: 0.5,
                mt: 0.3,
                p: 0.3,
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                borderRadius: '3px',
              }}
            >
              <Typography variant="caption" sx={{ fontSize: '9px', fontWeight: 500, marginRight: 1 }}>
                🖱️
                {' '}
                {mousePercentage}
                %
              </Typography>
              <Typography variant="caption" sx={{ fontSize: '9px', fontWeight: 500, marginRight: 1 }}>
                ⌨️
                {' '}
                {keyboardPercentage}
                %
              </Typography>
              <Typography variant="caption" sx={{ fontSize: '9px', fontWeight: 500 }}>
                📅
                {' '}
                {meetingPercentage}
                %
              </Typography>
            </Box>
          </Box>
        </Popover>
      </article>

      {/* {screensAreOpen && ( */}
      {/*  <ImageViewer */}
      {/*    disableScroll */}
      {/*    src={coverImageUrl} */}
      {/*    currentIndex={0} */}
      {/*    onClose={() => setScreensAreOpen(false)} */}
      {/*    backgroundStyle={{ backgroundColor: 'rgba(0,0,0,0.9)', zIndex: 20 }} */}
      {/*    closeOnClickOutside */}
      {/*  /> */}
      {/* )} */}

      {(screenshots && screenshots.length > 0 && screensAreOpen) && (
        <ScreenshotViewer
          coverImage={coverImageUrl}
          screenshots={screenshots}
          memberId={memberId}
          activityPercentage={activityPercentage}
          formattedTime={formattedTime}
          setScreensAreOpen={setScreensAreOpen}
          screensAreOpen={screensAreOpen}
        />
      )}
    </>
  );
}

export default Snapshot;

Snapshot.propTypes = {
  snapshot: PropTypes.shape({
    data: ActivityType,
    localBlockTime: PropTypes.string.isRequired,
    localStartTime: PropTypes.string.isRequired,
    roundedEndTime: PropTypes.string.isRequired,
    roundedStartTime: PropTypes.string.isRequired,
    isManual: PropTypes.bool.isRequired,
  }),
  blurScreenshots: PropTypes.bool,
  isAdmin: PropTypes.bool,
  onDeleteActivity: PropTypes.func,
  deleting: PropTypes.bool, // Added prop type validation for deleting
};
